
import React, { useEffect, useState } from "react";
import "../../Css/YourOrder.css";
import axios from "axios";

import { useCartContext } from "../../context/addToCart";
import { RxCross2 } from "react-icons/rx";
import { useNavigate } from "react-router-dom";
import { message } from "antd";

import Successbtn from "../successandfailed/Successbtn";
import emptyCart from "../../Images/add_to_cart.jpg";
import { useCurrency } from "../../services/locationService";
import { API_BASE_URL } from "../../services/apiConfig";

import {
  createPaymentOrder,
  verifyPayment,
  cancelPayment,
} from "../../services/paymentApi";

const PayOrder = ({
  selectedAddress,
  setSelectedAddress,
  allAddressData,
  setAlladdressData,
}) => {
  const { format, code, country, countryCode, rate, exchangeRateUpdatedAt } = useCurrency();
  const { cartValue, setCartCount, setCartValue } = useCartContext();

  const navigate = useNavigate();

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [wantsGstInvoice, setWantsGstInvoice] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [gstValidationError, setGstValidationError] = useState("");
  const [serviceability, setServiceability] = useState(null);

  const tokenid = localStorage.getItem("access_token");

  useEffect(() => {
    const selectedPincode = String(
      selectedAddress?.postal_code || selectedAddress?.postalCode || ""
    ).trim();

    if (!selectedPincode) {
      setServiceability(null);
      return undefined;
    }

    let isActive = true;
    setServiceability({ pincode: selectedPincode, loading: true });

    axios
      .get(`${API_BASE_URL}blue-dart/check-serviceability?pincode=${encodeURIComponent(selectedPincode)}`)
      .then((response) => {
        if (!isActive) return;

        const result = response.data;
        const isServiceable = result?.success && result?.data?.is_serviceable === true;
        setServiceability({
          pincode: selectedPincode,
          loading: false,
          isServiceable,
          message: isServiceable
            ? result?.data?.message || "Delivery is available for this address."
            : result?.data?.message || "Delivery is not available for this address.",
        });
      })
      .catch(() => {
        if (!isActive) return;
        setServiceability({
          pincode: selectedPincode,
          loading: false,
          isServiceable: false,
          message: "Unable to verify delivery for this address. Please try again.",
        });
      });

    return () => {
      isActive = false;
    };
  }, [selectedAddress]);

  // =========================================================
  // GET PROFILE DATA
  // =========================================================

  let profileData = {};

  try {
    profileData =
      JSON.parse(localStorage.getItem("profile")) || {};
  } catch (error) {
    console.error("Profile JSON error:", error);
    profileData = {};
  }

  const getCheckoutErrorMessage = (error, fallbackMessage) => {
    const responseData = error?.response?.data;
    const validationErrors = responseData?.errors;

    if (Array.isArray(validationErrors)) {
      const details = validationErrors.filter((item) => typeof item === "string");
      if (details.length) return details.join(" ");
    }

    if (validationErrors && typeof validationErrors === "object") {
      const details = Object.values(validationErrors)
        .flatMap((item) => Array.isArray(item) ? item : [item])
        .filter((item) => typeof item === "string");
      if (details.length) return details.join(" ");
    }

    if (typeof responseData?.message === "string" && responseData.message.trim()) {
      return responseData.message;
    }

    return error?.message || fallbackMessage;
  };

  // =========================================================
  // REMOVE PRODUCT FROM CART
  // =========================================================

  const removeCartData = (index) => {
    setCartValue((prevData) => {
      const updatedData = [...prevData];

      updatedData.splice(index, 1);

      localStorage.setItem(
        "cart",
        JSON.stringify(updatedData)
      );

      setCartCount(updatedData.length);

      return updatedData;
    });
  };

  // =========================================================
  // VALIDATE CHECKOUT
  // =========================================================

  const validateCheckout = () => {
    if (!tokenid) {
      message.error(
        "Please log in first to place the order."
      );
      return false;
    }

    if (!selectedAddress) {
      message.error(
        "Please select a delivery address."
      );
      return false;
    }

    if (!serviceability || serviceability.loading) {
      message.error("Please wait while delivery availability is checked.");
      return false;
    }

    if (!serviceability.isServiceable) {
      message.error(serviceability.message || "Delivery is not available for this address.");
      return false;
    }

    if (!cartValue || cartValue.length === 0) {
      message.error("Your cart is empty.");
      return false;
    }

    if (wantsGstInvoice) {
      const hasBusinessName = Boolean(businessName.trim());
      const normalizedGstNumber = gstNumber.trim().toUpperCase();
      const hasGstNumber = Boolean(normalizedGstNumber);

      if (hasBusinessName !== hasGstNumber) {
        const error = "To request a GST invoice, enter both the business name and GST number. Leave both blank to continue without one.";
        setGstValidationError(error);
        message.error(error);
        return false;
      }

      if (hasGstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(normalizedGstNumber)) {
        const error = "Enter a valid 15-character GSTIN, or clear the field to continue without a GST invoice.";
        setGstValidationError(error);
        message.error(error);
        return false;
      }
    }

    setGstValidationError("");
    return true;
  };

  // =========================================================
  // LOAD RAZORPAY SCRIPT
  // =========================================================

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const scriptId = "razorpay-checkout-script";

      const existingScript =
        document.getElementById(scriptId);

      // Script already exists
      if (existingScript) {
        if (window.Razorpay) {
          resolve(true);
          return;
        }

        existingScript.addEventListener(
          "load",
          () => resolve(true),
          { once: true }
        );

        existingScript.addEventListener(
          "error",
          () => resolve(false),
          { once: true }
        );

        return;
      }

      // Create Razorpay script
      const script = document.createElement("script");

      script.id = scriptId;

      script.src =
        "https://checkout.razorpay.com/v1/checkout.js";

      script.onload = () => resolve(true);

      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  // =========================================================
  // CANCEL LOCAL ORDER
  // =========================================================

  const handleCancelPayment = async (localOrderId) => {
    if (!localOrderId || !tokenid) {
      return;
    }

    try {
      await cancelPayment(
        localOrderId,
        tokenid
      );
    } catch (error) {
      console.error(
        "Cancel payment error:",
        error
      );
    }
  };

  // =========================================================
  // RAZORPAY PAYMENT
  // =========================================================

  const handleRazorpayPayment = async () => {
    if (isProcessingPayment) {
      return;
    }

    // Validate checkout
    if (!validateCheckout()) {
      return;
    }

    try {
      setIsProcessingPayment(true);

      // =====================================================
      // PREPARE CART ITEMS
      // =====================================================

      const items = cartValue.map((item) => ({
        product_id: Number(item.productid),

        quantity: Number(
          item.productQuantity || 1
        ),

        color_id:
          item.colorid !== undefined &&
          item.colorid !== null &&
          item.colorid !== ""
            ? Number(item.colorid)
            : null,

        size_id:
          item.sizeid !== undefined &&
          item.sizeid !== null &&
          item.sizeid !== ""
            ? Number(item.sizeid)
            : null,
      }));
      const baseSubtotal = cartValue.reduce(
        (total, item) => total + (Number(item.fixedPrice) || 0) * (Number(item.productQuantity) || 1),
        0
      );

      // Check product IDs
      const invalidProduct = items.some(
        (item) =>
          !item.product_id ||
          item.product_id <= 0
      );

      if (invalidProduct) {
        throw new Error(
          "Invalid product information in cart."
        );
      }

      // =====================================================
      // ADDRESS ID
      // =====================================================

      const addressId =
        selectedAddress?.address_id ??
        selectedAddress?.id;

      if (!addressId) {
        throw new Error(
          "Invalid delivery address."
        );
      }

      // =====================================================
      // CREATE BACKEND ORDER
      // =====================================================

      const orderResponse =
        await createPaymentOrder(
          items,
          addressId,
          tokenid,
          wantsGstInvoice && businessName.trim() && gstNumber.trim()
            ? {
                business_name: businessName.trim(),
                gst_number: gstNumber.trim().toUpperCase(),
              }
            : null,
          {
            code,
            country,
            countryCode,
            rate,
            rateUpdatedAt: exchangeRateUpdatedAt,
            baseSubtotal,
            displaySubtotal: Number((baseSubtotal * rate).toFixed(2)),
          }
        );

      console.log(
        "Create order response:",
        orderResponse
      );

      if (
        !orderResponse?.success ||
        !orderResponse?.razorpay
      ) {
        throw new Error(
          orderResponse?.message ||
            "Unable to create Razorpay order."
        );
      }

      // =====================================================
      // LOCAL DATABASE ORDER ID
      // =====================================================

      const localOrderId =
        orderResponse?.order_id ||
        orderResponse?.order?.id;

      if (!localOrderId) {
        throw new Error(
          "Local order ID was not returned by server."
        );
      }

      // =====================================================
      // LOAD RAZORPAY SDK
      // =====================================================

      const scriptLoaded =
        await loadRazorpayScript();

      if (
        !scriptLoaded ||
        !window.Razorpay
      ) {
        throw new Error(
          "Razorpay SDK failed to load."
        );
      }

      // =====================================================
      // RAZORPAY DATA
      // =====================================================

      const { razorpay } =
        orderResponse;

      // =====================================================
      // RAZORPAY OPTIONS
      // =====================================================

      const options = {
        key: razorpay.key_id,

        amount: razorpay.amount,

        currency: razorpay.currency,

        name: "1Five",

        description:
          "Order Payment",

        order_id:
          razorpay.order_id,

        // ===================================================
        // PREFILL
        // ===================================================

        prefill: {
          name:
            profileData?.name ||
            profileData?.full_name ||
            "",

          email:
            profileData?.email ||
            "",

          contact:
            profileData?.phone ||
            profileData?.mobile ||
            "",
        },

        // ===================================================
        // THEME
        // ===================================================

        theme: {
          color: "#1F1F1F",
        },

        // ===================================================
        // PAYMENT SUCCESS
        // ===================================================

        handler: async function (
          response
        ) {
          console.log(
            "Razorpay payment response:",
            response
          );

          try {
            // ===============================================
            // VERIFY PAYMENT
            // ===============================================

            const verifyResponse =
              await verifyPayment(
                {
                  razorpay_order_id:
                    response.razorpay_order_id,

                  razorpay_payment_id:
                    response.razorpay_payment_id,

                  razorpay_signature:
                    response.razorpay_signature,
                },
                tokenid
              );

            console.log(
              "Verify payment response:",
              verifyResponse
            );

            // ===============================================
            // CHECK PAYMENT STATUS
            // ===============================================

            const paymentIsSuccessful =
              verifyResponse?.success === true &&
              (
                verifyResponse?.payment_status ===
                  "Paid" ||

                verifyResponse?.order?.payment_status ===
                  "Paid"
              );

            if (!paymentIsSuccessful) {
              message.error(
                verifyResponse?.message ||
                  "Payment verification failed."
              );

              setIsProcessingPayment(false);

              return;
            }

            // ===============================================
            // PAYMENT SUCCESS
            // ===============================================

            message.success(
              "Payment successful."
            );

            // ===============================================
            // CLEAR CART
            // ===============================================

            setCartValue([]);

            setCartCount(0);

            localStorage.removeItem(
              "cart"
            );

            // ===============================================
            // GET ORDER ID
            // ===============================================

            const successOrderId =
              verifyResponse?.order_id ||
              verifyResponse?.order?.id ||
              localOrderId;

            // ===============================================
            // REDIRECT TO SUCCESS PAGE
            // ===============================================

            navigate(
              `/order-success/${successOrderId}`,
              {
                replace: true,
              }
            );

          } catch (verifyError) {
            console.error(
              "Razorpay verify error:",
              verifyError
            );

            message.error(
              getCheckoutErrorMessage(
                verifyError,
                "Payment verification failed. Please contact support."
              )
            );

            setIsProcessingPayment(false);
          }
        },

        // ===================================================
        // PAYMENT FAILED EVENT
        // ===================================================

        "payment.failed": async function (
          response
        ) {
          console.error(
            "Razorpay payment failed:",
            response
          );

          message.error(
            response?.error?.description ||
              "Payment failed."
          );

          await handleCancelPayment(
            localOrderId
          );

          setIsProcessingPayment(false);

          // Optional failed-page redirect
          navigate(
            `/order-failed/${localOrderId}`,
            {
              replace: true,
            }
          );
        },

        // ===================================================
        // MODAL DISMISSED
        // ===================================================

        modal: {
          ondismiss: async () => {
            console.log(
              "Razorpay modal dismissed."
            );

            message.info(
              "Payment cancelled."
            );

            await handleCancelPayment(
              localOrderId
            );

            setIsProcessingPayment(false);
          },
        },
      };

      // =====================================================
      // CREATE RAZORPAY INSTANCE
      // =====================================================

      const razorpayInstance =
        new window.Razorpay(options);

      // =====================================================
      // OPEN RAZORPAY
      // =====================================================

      razorpayInstance.open();

    } catch (paymentError) {
      console.error(
        "Payment error:",
        paymentError
      );

      message.error(
        getCheckoutErrorMessage(
          paymentError,
          "Something went wrong while creating the payment."
        )
      );

      setIsProcessingPayment(false);
    }
  };

  // =========================================================
  // CALCULATE SUBTOTAL
  // =========================================================

  let finalsubtotal = 0;

  cartValue.forEach((product) => {
    const price =
      Number(product?.fixedPrice) || 0;

    const quantity =
      Number(product?.productQuantity) || 0;

    finalsubtotal +=
      price * quantity;
  });

  finalsubtotal =
    Math.round(finalsubtotal);

  // =========================================================
  // GST INCLUDED IN PRODUCT PRICE
  // =========================================================

  const roundedBasePrice =
    Math.round(
      finalsubtotal / 1.18
    );

  const totaltax =
    Math.round(
      finalsubtotal -
        roundedBasePrice
    );

  // =========================================================
  // DELIVERY CHARGE
  // Backend:
  //
  // subtotal >= 999 => FREE
  // subtotal < 999  => ₹50
  // =========================================================

  const deliveryCharges =
    finalsubtotal >= 999
      ? 0
      : 50;

  // =========================================================
  // TOTAL
  // =========================================================

  const totalamount =
    finalsubtotal +
    deliveryCharges;

  // =========================================================
  // EMPTY CART
  // =========================================================

  if (
    !cartValue ||
    cartValue.length === 0
  ) {
    return (
      <div className="YourOrder-Con-Main">

        <div
          className="txt"
          id="txt"
        >
          <b>1</b>
          <b>F</b>
          <b>I</b>
          <b>V</b>
          <b>E</b>
        </div>

        <div className="checkout-page-cart-empty-imaeg-main-con">

          <div className="checkout-page-cart-empty-imaeg-con">

            <img
              src={emptyCart}
              alt="emptyCart"
            />

          </div>

        </div>

        <div className="plz-add-product-into-cart">

          <h3>
            Add Product Into Cart
          </h3>

        </div>

      </div>
    );
  }

  // =========================================================
  // MAIN UI
  // =========================================================

  return (
    <div className="YourOrder-Con-Main">

      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="YourOrder-Con-head-Main">

        <h2>
          Your Order
        </h2>

      </div>

      {/* ===================================================
          PRODUCT / TOTAL TITLE
      =================================================== */}

      <div className="YourOrder-Con-Title-Main">

        <h2>
          Product
        </h2>

        <h2>
          Total
        </h2>

      </div>

      {/* ===================================================
          CART PRODUCTS
      =================================================== */}

      <div className="Product-addedCart-Side-Bar">

        {cartValue.map(
          (elem, index) => (

            <div
              className="Product-addedCart-Side-details"
              key={
                elem?.productid ||
                index
              }
            >

              {/* PRODUCT IMAGE */}

              <div className="Product-addedCart-Image-con">

                <div className="Product-addedCart-Image">

                  <img
                    src={
                      elem.productImage
                    }
                    alt={
                      elem.productName
                    }
                  />

                </div>

              </div>

              {/* PRODUCT DETAILS */}

              <div className="Product-details-addedCart-price-name">

                <h2>
                  {
                    elem.productName
                  }
                </h2>

                {/* SIZE */}

                {elem?.productSize != 0 &&
                  elem?.productSize ? (

                  <h3>
                    <span>
                      Size :{" "}
                      {
                        elem.productSize
                      }
                    </span>
                  </h3>

                ) : null}

                {/* QUANTITY */}

                <h3>
                  <span>
                    Quantity :{" "}
                    {
                      elem.productQuantity
                    }
                  </span>
                </h3>

                {/* COLOR */}

                {elem?.productColor ? (

                  <h3>
                    <span>
                      Color :{" "}
                      {
                        elem.productColor
                      }
                    </span>
                  </h3>

                ) : null}

                {/* PRICE */}

                <h3>

                  <span>
                    Price :{" "}
                  </span>

                  {format(elem.fixedPrice)}

                </h3>

              </div>

              {/* REMOVE */}

              <div className="Product-details-addedCart-cancel-btn">

                <div
                  className="Product-details-addedCart-cancel-btn-con"
                  onClick={() =>
                    removeCartData(index)
                  }
                >

                  <RxCross2 />

                </div>

              </div>

            </div>

          )
        )}

        {/* =================================================
            ORDER SUMMARY
        ================================================= */}

        <div className="Product-addedCart-Side-details-1">

          <h3>
            Hassle-free shopping – Price includes
            18% GST!
          </h3>

          <div className="gst-invoice-option">
            <label htmlFor="gst-invoice-checkbox">
              <input
                id="gst-invoice-checkbox"
                type="checkbox"
                checked={wantsGstInvoice}
                onChange={(event) => {
                  setWantsGstInvoice(event.target.checked);
                  setGstValidationError("");
                }}
              />
              Request GST invoice (optional)
            </label>
          </div>

          {wantsGstInvoice && (
            <div className="gst-invoice-fields">
              <p className="gst-invoice-optional-note">
                Leave both fields blank to continue without a GST invoice.
              </p>
              <label htmlFor="gst-business-name">Business name (optional)</label>
              <input
                id="gst-business-name"
                type="text"
                autoComplete="organization"
                value={businessName}
                onChange={(event) => {
                  setBusinessName(event.target.value);
                  setGstValidationError("");
                }}
              />

              <label htmlFor="gst-number">GST number (optional)</label>
              <input
                id="gst-number"
                type="text"
                autoComplete="off"
                maxLength={15}
                value={gstNumber}
                onChange={(event) => {
                  setGstNumber(event.target.value.toUpperCase());
                  setGstValidationError("");
                }}
              />
              {gstValidationError && (
                <p className="gst-invoice-validation-error" role="alert">
                  {gstValidationError}
                </p>
              )}
            </div>
          )}

          {/* SUBTOTAL */}

          <div className="YourOrder-Con-details-Main">

            <div className="YourOrder-Con-details-text">

              <h2 className="YourOrder-totoal">
                Subtotal
              </h2>

            </div>

            <h2>
              {format(finalsubtotal)}
            </h2>

          </div>

          {/* DELIVERY */}

          <div className="YourOrder-Con-details-Main">

            <div className="YourOrder-Con-details-text">

              <h2 className="YourOrder-totoal">
                Delivery Charges
              </h2>

            </div>

            <h2>

              {deliveryCharges === 0
                ? "Free"
                : format(deliveryCharges)}

            </h2>

          </div>

          {/* BASE PRICE */}

          <div className="YourOrder-Con-details-Main">

            <div className="YourOrder-Con-details-text">

              <h2 className="YourOrder-totoal">
                Base Price
              </h2>

            </div>

            <h2>
              {format(roundedBasePrice)}
            </h2>

          </div>

          {/* GST */}

          <div className="YourOrder-Con-details-Main">

            <div className="YourOrder-Con-details-text">

              <h2 className="YourOrder-totoal">
                Tax
              </h2>

            </div>

            <h2>
              {format(totaltax)}
            </h2>

          </div>

          {/* TOTAL */}

          <div className="YourOrder-Con-details-Main">

            <div className="YourOrder-Con-details-text">

              <h2 className="YourOrder-totoal">
                Total Amount
              </h2>

            </div>

            <h2>
              {format(Math.round(totalamount))}
            </h2>

          </div>

        </div>

        {/* =================================================
            PAYMENT BUTTON
        ================================================= */}

        {serviceability && (
          <div className={`checkout-serviceability ${serviceability.isServiceable ? "available" : "unavailable"}`} role="status">
            <strong>Delivery to {serviceability.pincode}</strong>
            <span>
              {serviceability.loading ? "Checking delivery availability..." : serviceability.message}
            </span>
          </div>
        )}

        <div className="Product-details-Addedcart-btn">

          <div className="Product-details-Addedcart-btn-ViewCart">

            <button
              type="button"
              onClick={
                handleRazorpayPayment
              }
              disabled={
                isProcessingPayment || serviceability?.loading || !serviceability?.isServiceable
              }
            >

              {isProcessingPayment
                ? "Processing Payment..."
                : "Place Your Order and Pay"}

            </button>

          </div>

        </div>

      </div>

    </div>
  );
};

export default PayOrder;
